import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { TaskView } from "@/pages/Tasks/TaskView";
import { NewTaskModal } from "@/pages/Tasks/NewTaskModal";
import { useNavigate } from "react-router-dom";
import { taskStatusesApi, taskTypesApi } from "@/lib/api";

export interface TaskViewModalProps {
    isOpen: boolean;
    onClose: (hasChanged?: boolean) => void;
    task: any;
    onEdit?: (task: any) => void;
    onUpdated?: (task: any) => void;
}

export const TaskViewModal: React.FC<TaskViewModalProps> = ({
    isOpen,
    onClose,
    task,
    onEdit,
    onUpdated,
}) => {
    const { t } = useTranslation("global");
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [statuses, setStatuses] = useState<any[]>([]);
    const [taskTypes, setTaskTypes] = useState<any[]>([]);
    const [hasChanged, setHasChanged] = useState<boolean>(false);

    useEffect(() => {
        setIsEditing(false);
        setHasChanged(false);
    }, [isOpen, task]);

    useEffect(() => {
        if (isOpen) {
            taskStatusesApi
                .list()
                .then((res) => {
                    if (res.result && Array.isArray(res.data)) {
                        setStatuses(res.data);
                    }
                })
                .catch(console.error);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) taskTypesApi.list().then((res) => {
            if (res.result && Array.isArray(res.data)) setTaskTypes(res.data);
        }).catch(console.error);
    }, [isOpen]);

    if (!isOpen || !task) {
        return null;
    }

    const handleOpenFull = () => {
        onClose(hasChanged);
        navigate(`/tasks/${task?.id}`);
    };

    const handleClose = () => {
        setIsEditing(false);
        onClose(hasChanged);
    };

    const handleTaskUpdated = (updatedTask: any) => {
        setHasChanged(true);
        if (onUpdated) {
            onUpdated(updatedTask);
        }
    };

    if (isEditing) {
        return (
            <NewTaskModal
                isOpen={isOpen}
                onClose={() => {
                    setIsEditing(false);
                }}
                task={task}
                onSuccess={(updatedTask) => {
                    setIsEditing(false);
                    setHasChanged(true);
                    if (onUpdated) {
                        onUpdated(updatedTask);
                    }
                    onClose(true);
                }}
                statuses={statuses}
                types={taskTypes}
            />
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            maxWidth="4xl"
            //title={`${task?.code} - ${task?.title}`}
            title={t("tasks.details")}
            footer={
                <>
                    <button
                        type="button"
                        className="w-[90px] btn secondary flex items-center justify-center gap-1.5"
                        onClick={handleClose}
                    >
                        {t("common.close")}
                    </button>
                    <button
                        type="button"
                        className="w-[90px] btn secondary flex items-center justify-center gap-1.5"
                        onClick={handleOpenFull}
                    >
                        {t("tasks.modal_view.open_full")}
                    </button>
                </>
            }
        >
            <div>
                <TaskView
                    taskId={task?.id}
                    taskCode={task?.code}
                    onEdit={(t) => {
                        setIsEditing(true);
                        if (onEdit) {
                            onEdit(t);
                        }
                    }}
                    onTaskUpdate={handleTaskUpdated}
                />
            </div>
        </Modal>
    );
};
